import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { EventqBrowserTab } from '../browser-tab/browser-tab';

export interface EventqBrowserTabItem {
  id: string;
  label: string;
  tags?: string[];
  /** `HH:MM:SS` processing runtime; omit to hide the Runtime pill. */
  runtimeLabel?: string | null;
}

@Component({
  selector: 'eventq-browser-tab-bar',
  imports: [EventqBrowserTab, MatIcon],
  templateUrl: './browser-tab-bar.html',
  styleUrl: './browser-tab-bar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqBrowserTabBar {
  readonly tabs = input.required<EventqBrowserTabItem[]>();
  readonly activeTabId = input.required<string>();
  readonly expanded = input(false);
  readonly addDisabled = input(false);
  /** Single-tab popout window — hides add control and tab switching chrome. */
  readonly popoutMode = input(false);
  /** When false, hides expand / popout controls on every tab. */
  readonly expandEnabled = input(true);
  /** Tab ids that may show a close control (events landing, multi-tab only). */
  readonly closeableTabIds = input<readonly string[]>([]);

  readonly tabSelect = output<string>();
  readonly tabAdd = output<void>();
  readonly tabExpand = output<void>();
  readonly tabClose = output<string>();

  readonly hoveredTabId = signal<string | null>(null);
  private readonly tabsScroll = viewChild<ElementRef<HTMLElement>>('tabsScroll');
  private readonly addButton = viewChild<ElementRef<HTMLButtonElement>>('addButton');

  /** Host element for the add (+) button, or null when the control is hidden. */
  readonly addButtonHost = computed(() => this.addButton()?.nativeElement ?? null);

  constructor() {
    effect(() => {
      const activeId = this.activeTabId();
      untracked(() => {
        queueMicrotask(() => this.scrollActiveTabIntoView(activeId));
      });
    });
  }

  onTabHover(tabId: string | null): void {
    this.hoveredTabId.set(tabId);
  }

  onTabSelect(tabId: string): void {
    if (this.popoutMode()) {
      return;
    }

    this.hoveredTabId.set(null);
    this.tabSelect.emit(tabId);
  }

  /** Maps vertical wheel/trackpad gestures to horizontal tab scrolling. */
  onTabsWheel(event: WheelEvent): void {
    const element = event.currentTarget;
    if (!(element instanceof HTMLElement) || element.scrollWidth <= element.clientWidth) {
      return;
    }

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) {
      return;
    }

    event.preventDefault();
    element.scrollLeft += delta;
  }

  private scrollActiveTabIntoView(activeId: string): void {
    const container = this.tabsScroll()?.nativeElement;
    if (!container) {
      return;
    }

    const tabEl = container.querySelector(`[data-tab-id="${activeId}"]`);
    if (tabEl && 'scrollIntoView' in tabEl && typeof tabEl.scrollIntoView === 'function') {
      tabEl.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }
}
